import blogContent from "@/data/blog-content.json";

export type PostMeta = {
  slug: string;
  title: string;
  seoTitle?: string;
  excerpt: string;
  readMinutes: number;
  category: string;
  image: string;
  /** ISO dates: added to this site / last content change. */
  datePublished: string;
  dateModified: string;
};

export type ArticleLink = { text: string; href: string };
export type ArticleBlock = {
  t: "h1" | "h2" | "h3" | "p";
  v: string;
  /** Optional internal links: wraps the first occurrence of `text` in `v`. */
  links?: ArticleLink[];
};

export const SITE_URL = "https://preserveearth.co.uk";
export const absoluteUrl = (path: string) =>
  path.startsWith("http") ? path : `${SITE_URL}${path}`;
export type ArticleContent = {
  title: string;
  metaDesc: string;
  blocks: ArticleBlock[];
};

export const posts: PostMeta[] = [
  {
    slug: "business-laptop-it-disposal-checklist",
    seoTitle: "Business Laptop and IT Disposal Checklist | Preserve Earth",
    title: "Business laptop and IT equipment disposal: a practical checklist",
    excerpt:
      "A step by step checklist for retiring business laptops and IT kit: leased devices, asset lists, data wiping or physical destruction, and records.",
    readMinutes: 6,
    category: "IT and Media Recycling",
    image: "/images/blog/business-laptop-it-disposal-checklist-hero.webp",
    datePublished: "2026-11-04",
    dateModified: "2026-11-04",
  },
  {
    slug: "certificate-of-destruction-guide",
    seoTitle: "Certificate of Destruction: A Business Guide | Preserve Earth",
    title: "What is a certificate of destruction, and how should your business keep one?",
    excerpt:
      "What a certificate of destruction is, what it should include, why your business needs one and how to file it so you can show what was destroyed.",
    readMinutes: 6,
    category: "Data Protection",
    image: "/images/blog/certificate-of-destruction-guide-hero.webp",
    datePublished: "2026-11-02",
    dateModified: "2026-11-02",
  },
  {
    slug: "eu-textile-epr-uk-firms-eu-sites-uniforms",
    seoTitle: "EU Textile EPR for UK Firms with EU Sites | Preserve Earth",
    title: "EU textile EPR: what should UK firms with sites in Europe know?",
    excerpt:
      "MEPs voted on 20 October to ease EU textile EPR paperwork for small EU producers. What it means for UK firms disposing of uniforms at EU sites.",
    readMinutes: 6,
    category: "Textile Recycling",
    image: "/images/blog/eu-textile-epr-uk-firms-eu-sites-hero.webp",
    datePublished: "2026-10-28",
    dateModified: "2026-10-28",
  },
  {
  "slug": "ico-met-police-third-party-suppliers-disposal",
  "seoTitle": "ICO and the Met Police: Choosing Suppliers | Preserve Earth",
  "title": "What does the ICO's action against the Met Police mean for choosing your suppliers?",
  "excerpt": "The ICO's action against the Met Police is about unapproved third-party tools. Why the same checks apply to whoever destroys your confidential waste.",
  "readMinutes": 6,
  "category": "Data Protection",
  "image": "/images/blog/ico-met-police-third-party-suppliers-hero.webp",
  "datePublished": "2026-10-26",
  "dateModified": "2026-10-26"
},
  {
  "slug": "branded-uniforms-textile-banks-secure-disposal",
  "seoTitle": "Branded Uniforms in Textile Banks? | Preserve Earth",
  "title": "Can your old branded uniforms go in a textile bank?",
  "excerpt": "A £1,000 fine beside a Warminster clothing bank shows how textile banks are struggling. Why branded workwear needs secure disposal instead.",
  "readMinutes": 6,
  "category": "Textile Recycling",
  "image": "/images/blog/branded-uniforms-textile-banks-hero.webp",
  "datePublished": "2026-10-24",
  "dateModified": "2026-10-24"
},
  {
  "slug": "office-laptops-lithium-batteries-safe-disposal",
  "seoTitle": "Office Laptop and Battery Disposal | Preserve Earth",
  "title": "Old laptops and lithium batteries: how should your office dispose of them safely?",
  "excerpt": "A battery in the wrong bin is suspected of starting a fire at a Shrewsbury recycling site. How UK firms should store and dispose of old laptops safely.",
  "readMinutes": 5,
  "category": "IT and Media Recycling",
  "image": "/images/blog/office-laptops-lithium-batteries-hero.webp",
  "datePublished": "2026-10-22",
  "dateModified": "2026-10-22"
},
  {
    slug: "confidential-paper-binned-by-mistake-zagreb-lesson",
    seoTitle: "Confidential Paper Binned by Mistake | Preserve Earth",
    title: "Confidential papers ended up in a street bin. Could it happen in your office?",
    excerpt:
      "Zagreb staff records meant for shredding ended up in a street bin. What UK firms should do with confidential paper while it waits for destruction.",
    readMinutes: 5,
    category: "Data Protection",
    image: "/images/blog/confidential-paper-binned-by-mistake-hero.webp",
    datePublished: "2026-10-20",
    dateModified: "2026-10-20",
  },
  {
    slug: "ico-confidential-waste-disposal-guide",
    seoTitle: "ICO Confidential Waste Disposal Guide | Preserve Earth",
    title: "What does the ICO expect when you dispose of confidential waste?",
    excerpt:
      "A plain-English guide to what the ICO expects when you dispose of confidential paper and old devices, from locked bins and cross-cut shredding to certificates of destruction, disposal logs and the 72 hour breach reporting window.",
    readMinutes: 6,
    category: "Data Protection",
    image:
      "/images/blog/ico-confidential-waste-hero.webp",
    datePublished: "2026-10-19",
    dateModified: "2026-10-19",
  },
  {
    slug: "new-legislation-around-responsible-recycling-of-textiles-for-businesses-in-2026",
    seoTitle: "Textile Recycling Legislation 2026 | Preserve Earth",
    title:
      "New legislation around responsible recycling of textiles for businesses in 2026",
    excerpt:
      "A practical guide to UK and EU textile regulation, forthcoming EU EPR obligations and the records UK businesses should keep.",
    readMinutes: 6,
    category: "Compliance",
    image:
      "/images/blog/blog-hero-image-1775243133271.webp",
    datePublished: "2026-04-03",
    dateModified: "2026-10-12",
  },
  {
    slug: "textile-and-uniform-recycling-services-for-corporate-waste-reduction-uk",
    seoTitle: "Uniform and PPE Recycling: A Business Guide | Preserve Earth",
    title: "How can businesses recycle uniforms, PPE and surplus textiles? A UK guide",
    excerpt:
      "A UK guide to recycling branded uniforms, workwear, PPE and surplus textiles securely, from collection and sorting to shredding and audit records.",
    readMinutes: 7,
    category: "Textile Recycling",
    image:
      "/images/blog/blog-hero-image-1775236550523.webp",
    datePublished: "2026-04-03",
    dateModified: "2026-10-20",
  },
  {
    slug: "how-to-dispose-of-branded-stock-and-unsold-inventory-securely-and-sustainably",
    seoTitle: "Branded Stock Disposal: Secure & Sustainable",
    title:
      "How to Dispose of Branded Stock and Unsold Inventory Securely and Sustainably",
    excerpt:
      "A practical guide for UK businesses balancing brand protection, compliance and environmental impact when retiring unsold or recalled inventory.",
    readMinutes: 6,
    category: "Brand Protection",
    image:
      "/images/blog/blog-hero-image-1775234418927.webp",
    datePublished: "2026-04-03",
    dateModified: "2026-06-12",
  },
  {
    slug: "textile-waste-recycling-make-a-big-impact-today",
    seoTitle: "How Can Businesses Reduce Textile Waste? | Preserve Earth",
    title: "How can businesses reduce textile waste? A practical guide to recycling fabrics",
    excerpt:
      "Practical ways businesses can cut textile waste, from on-site fabric recycling bins to answers on cost and process, and why it matters for sustainability.",
    readMinutes: 4,
    category: "Sustainability",
    image:
      "/images/blog/blog-clothes-pile---warehouse.webp",
    datePublished: "2025-02-07",
    dateModified: "2026-10-26",
  },
  {
    slug: "epr-in-textiles-manufacturing-impact-today",
    title: "EPR in Textiles: Manufacturing Impact Today",
    excerpt:
      "How Extended Producer Responsibility is reshaping textile manufacturing today, and where UK sustainability efforts go from here.",
    readMinutes: 8,
    category: "EPR",
    image:
      "/images/optimised/blog-textiles-1280.webp",
    datePublished: "2024-12-09",
    dateModified: "2026-10-26",
  },
  {
    slug: "corporate-uniform-recycling-a-sustainable-solution-for-business-waste",
    seoTitle: "Why Shred Corporate Uniforms? A Guide | Preserve Earth",
    title: "Why shred retired corporate uniforms? A guide to secure, sustainable disposal",
    excerpt:
      "How shredding retired corporate uniforms protects your brand, cuts landfill waste and supports compliance, with steps for setting up a recycling programme.",
    readMinutes: 3,
    category: "Textile Recycling",
    image:
      "/images/blog/blog-download.webp",
    datePublished: "2024-10-31",
    dateModified: "2026-10-20",
  },
  {
    slug: "eco-friendly-ppe-disposal-for-businesses",
    seoTitle: "Eco-Friendly PPE Disposal | Preserve Earth",
    title:
      "Eco-Friendly PPE Disposal: How Preserve Earth Helps Your Business Go Green",
    excerpt:
      "Practical steps for retiring PPE and workwear through secure collection, brand protection, destruction and documented recycling outcomes.",
    readMinutes: 2,
    category: "PPE",
    image:
      "/images/blog/blog-2-1536x864.webp",
    datePublished: "2024-09-17",
    dateModified: "2026-10-13",
  },
];

const SRCSETS: Record<string, string> = {
  "/images/optimised/blog-textiles-1280.webp":
    "/images/optimised/blog-textiles-768.webp 768w, /images/optimised/blog-textiles-1280.webp 1280w",
  "/images/optimised/blog-andreas-gucklhorn-unsplash-1280.webp":
    "/images/optimised/blog-andreas-gucklhorn-unsplash-768.webp 768w, /images/optimised/blog-andreas-gucklhorn-unsplash-1280.webp 1280w",
};
/** Responsive srcset for posts that have optimised renditions. */
export const postSrcSet = (p: PostMeta) => SRCSETS[p.image];

const content = blogContent as Record<string, ArticleContent>;

export function getPostBySlug(slug: string): PostMeta | undefined {
  return posts.find((p) => p.slug === slug);
}

export function getArticleContent(slug: string): ArticleContent | undefined {
  return content[slug];
}
